import { z } from 'zod';

const MAX_ROWS = 500;

const agencyFields = z
  .object({
    name: z.string().trim().min(1).max(160),
    shortName: z.string().trim().min(1).max(80),
    city: z.string().trim().min(1).max(80),
    address: z.string().trim().min(1).max(500),
    phone: z.string().trim().min(1).max(40),
    whatsapp: z.string().trim().min(1).max(40),
    email: z.string().trim().min(1).max(191),
    rc: z.string().max(80).optional(),
    ice: z.string().max(80).optional(),
    patente: z.string().max(80).optional(),
    logoText: z.string().trim().min(1).max(80),
    currency: z.string().trim().max(8).optional(),
    rateEur: z.number().positive().max(100000).optional(),
    rateUsd: z.number().positive().max(100000).optional(),
    contractFooter: z.string().max(2000).optional(),
    pickupLocations: z.array(z.string().trim().min(1).max(80)).max(40).optional(),
    whatsappTemplate: z.string().max(2000).optional(),
  })
  .strict();

const clientRow = z
  .object({
    id: z.string().trim().min(1).max(64),
    name: z.string().trim().min(1).max(120),
    email: z.string().max(191).default(''),
    phone: z.string().max(40).default(''),
    licenseNumber: z.string().max(64).default(''),
    cinPassport: z.string().max(64).optional(),
    address: z.string().max(300).optional(),
    riskLevel: z.enum(['clean', 'watch', 'blacklisted']),
    riskNote: z.string().max(500).optional(),
    rentalsCount: z.number().int().nonnegative().max(100000).default(0),
    totalSpent: z.number().nonnegative().max(100000000).default(0),
    createdAt: z.string().max(40).default(''),
  })
  .strict();

const expenseRow = z
  .object({
    id: z.string().trim().min(1).max(64),
    vehicleId: z.string().max(64).optional(),
    category: z.enum(['maintenance', 'assurance', 'visite', 'vignette', 'fuel', 'cleaning', 'other']),
    label: z.string().trim().min(1).max(160),
    amount: z.number().nonnegative().max(100000000),
    date: z.string().max(40),
  })
  .strict();

const fineRow = z
  .object({
    id: z.string().trim().min(1).max(64),
    vehicleId: z.string().max(64).default(''),
    date: z.string().max(40),
    label: z.string().trim().min(1).max(160),
    amount: z.number().nonnegative().max(100000000),
    clientName: z.string().max(120).optional(),
    bookingId: z.string().max(64).optional(),
    status: z.enum(['unpaid', 'paid', 'disputed']),
    pvNumber: z.string().max(40).optional(),
  })
  .strict();

const cashRow = z
  .object({
    id: z.string().trim().min(1).max(64),
    date: z.string().max(40),
    type: z.enum(['in', 'out']),
    category: z.enum(['avance', 'solde', 'caution_rendue', 'caution_recue', 'depense', 'autre']),
    label: z.string().trim().min(1).max(160),
    amount: z.number().nonnegative().max(100000000),
    bookingId: z.string().max(64).optional(),
  })
  .strict();

const tariffRule = z
  .object({
    id: z.string().trim().min(1).max(64),
    name: z.string().trim().min(1).max(120),
    vehicleId: z.string().max(64),
    startDate: z.string().max(40),
    endDate: z.string().max(40),
    multiplier: z.number().positive().max(20),
  })
  .strict();

const discountTier = z
  .object({
    id: z.string().trim().min(1).max(64),
    minDays: z.number().int().positive().max(3650),
    percentOff: z.number().nonnegative().max(100),
  })
  .strict();

const extraItem = z
  .object({
    id: z.string().trim().min(1).max(64),
    name: z.string().trim().min(1).max(120),
    description: z.string().max(300).default(''),
    pricePerDay: z.number().nonnegative().max(1000000),
  })
  .strict();

export const deskImportSchema = z
  .object({
    version: z.number().int().optional(),
    exportedAt: z.string().max(40).optional(),
    agency: agencyFields.optional(),
    clients: z.array(clientRow).max(MAX_ROWS).optional(),
    expenses: z.array(expenseRow).max(MAX_ROWS).optional(),
    fines: z.array(fineRow).max(MAX_ROWS).optional(),
    movements: z.array(cashRow).max(MAX_ROWS).optional(),
    tariffRules: z.array(tariffRule).max(MAX_ROWS).optional(),
    discountTiers: z.array(discountTier).max(MAX_ROWS).optional(),
    extras: z.array(extraItem).max(MAX_ROWS).optional(),
    vehicles: z.array(z.unknown()).max(MAX_ROWS).optional(),
    bookings: z.array(z.unknown()).max(MAX_ROWS).optional(),
  })
  .strict();

export type DeskImportBundle = z.infer<typeof deskImportSchema>;

export function parseDeskImport(value: unknown): { ok: true; bundle: DeskImportBundle } | { ok: false; message: string } {
  const parsed = deskImportSchema.safeParse(value);
  if (!parsed.success) {
    return { ok: false, message: parsed.error.issues[0]?.message ?? 'The file does not match the office backup format.' };
  }
  const bundle = parsed.data;
  const blocks = [
    bundle.agency,
    bundle.clients,
    bundle.expenses,
    bundle.fines,
    bundle.movements,
    bundle.tariffRules,
    bundle.discountTiers,
    bundle.extras,
  ].filter((block) => block !== undefined);
  if (blocks.length === 0 && (bundle.vehicles?.length || bundle.bookings?.length)) {
    return {
      ok: false,
      message: 'This file only contains vehicles or bookings. Use the fleet CSV import for those rows.',
    };
  }
  if (blocks.length === 0) {
    return { ok: false, message: 'No office data was recognized in this file.' };
  }
  return { ok: true, bundle };
}

export function describeDeskImport(bundle: DeskImportBundle): string {
  const parts: string[] = [];
  if (bundle.agency) parts.push('the agency profile');
  if (bundle.clients) parts.push(`${bundle.clients.length} clients`);
  if (bundle.expenses) parts.push(`${bundle.expenses.length} expenses`);
  if (bundle.fines) parts.push(`${bundle.fines.length} fines`);
  if (bundle.movements) parts.push(`${bundle.movements.length} cash lines`);
  if (bundle.tariffRules) parts.push(`${bundle.tariffRules.length} season rules`);
  if (bundle.discountTiers) parts.push(`${bundle.discountTiers.length} length discounts`);
  if (bundle.extras) parts.push(`${bundle.extras.length} extras`);
  const skipped = (bundle.vehicles?.length ?? 0) + (bundle.bookings?.length ?? 0);
  const summary = parts.join(', ');
  if (skipped === 0) return summary;
  return `${summary}. ${skipped} vehicle or booking rows will be ignored`;
}
